import React from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import { SavedViewType } from "lib/generated/app-api";
import UIcon from "lib/icons/untitled_ui/UIcon";

type NoticeEmptyStateProps = {
  hydratedRecentViews: (SavedViewType | undefined)[];
  setSelectedView: (viewId: string, viewName: string, context: string) => void;
  setIsFilterDrawerOpen: (isOpen: boolean) => void;
};

function NoticeEmptyStateAction({
  hydratedRecentViews,
  setSelectedView,
  setIsFilterDrawerOpen,
}: NoticeEmptyStateProps) {
  return (
    <TemplateSection column recentViewsList={hydratedRecentViews.length > 1}>
      {hydratedRecentViews.length > 1 && (
        <RecentViewsList column>
          {hydratedRecentViews
            .filter((view): view is SavedViewType & { id: string } =>
              Boolean(view && view.id && view.name),
            )
            .slice(0, 2)
            .map((view) => (
              <Button
                buttonType="tertiary"
                key={view.id}
                onClick={() => {
                  setSelectedView(view.id, view.name, "Recent view");
                }}
              >
                {view.name}
              </Button>
            ))}
        </RecentViewsList>
      )}
      <Flex gap={14}>
        <Card
          width={255}
          padding={24}
          onClick={() => {
            setSelectedView(
              "b568b291-4567-496f-bbf8-8165002f2ce5",
              "Open & upcoming tenders",
              "Pre-tender and open tender notices matching signal settings.",
            );
            setIsFilterDrawerOpen(false);
          }}
        >
          <Flex column gap={16}>
            <Flex gap={8} alignItems="center">
              <UIcon icon="tenderPart" size={14} />
              <Text fontWeight="600" color="sysPrimaryDefault">
                Find open tenders
              </Text>
            </Flex>
            <Text description color="sysTextDefault">
              Search pre-tender and open tenders matching your keywords
            </Text>
          </Flex>
        </Card>
        <Card
          width={255}
          padding={24}
          onClick={() => {
            setSelectedView(
              "c735a82a-4fea-4a97-84a8-7f9638e64c25",
              "Upcoming contract expiries",
              "All contract awards matching your signal settings due to expire from today onward.",
            );
            setIsFilterDrawerOpen(false);
          }}
        >
          <Flex column gap={16}>
            <Flex gap={8} alignItems="center">
              <UIcon icon="pipelinePart" size={14} />
              <Text fontWeight="600" color="sysPrimaryDefault">
                Find expiring contracts
              </Text>
            </Flex>
            <Text description color="sysTextDefault">
              Search awarded contracts from relevant buyers to pre-engage
            </Text>
          </Flex>
        </Card>
      </Flex>
      <Flex gap={14}>
        <Card
          width={255}
          padding={24}
          onClick={() => {
            setSelectedView(
              "f911ba36-1042-4ca2-9cb6-f6ef2150f282",
              "Competitor activity",
              "Notices where your competitor is the named supplier or has been mentioned.",
            );
            setIsFilterDrawerOpen(false);
          }}
        >
          <Flex column gap={16}>
            <Flex gap={8} alignItems="center">
              <UIcon icon="together" size={14} />
              <Text fontWeight="600" color="sysPrimaryDefault">
                View competitor activity
              </Text>
            </Flex>
            <Text description color="sysTextDefault">
              Start with a view of notices your competitors are mentioned
            </Text>
          </Flex>
        </Card>
        <Card
          width={255}
          padding={24}
          onClick={() => {
            setSelectedView(
              "3bacb62c-938f-4d17-8fd6-01eaaa11b922",
              "Partner activity",
              "Notices where your partner is the named supplier or has been mentioned.",
            );
            setIsFilterDrawerOpen(false);
          }}
        >
          <Flex column gap={16}>
            <Flex gap={8} alignItems="center">
              <UIcon icon="together" size={14} />
              <Text fontWeight="600" color="sysPrimaryDefault">
                View partner activity
              </Text>
            </Flex>
            <Text description color="sysTextDefault">
              Search all notices where your partners are mentioned
            </Text>
          </Flex>
        </Card>
      </Flex>
    </TemplateSection>
  );
}

const TemplateSection = styled(Flex)(({ recentViewsList }: { recentViewsList: boolean }) => ({
  marginTop: recentViewsList ? 0 : 48,
  gap: 14,
  width: "524px",
}));

const RecentViewsList = styled(Flex)({
  marginBottom: 14,
});

export default NoticeEmptyStateAction;
